import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

interface ResourcePickerProps {
  type: string;
  value: string | null;
  onChange: (value: string, field: 'pageId' | 'productId' | 'collectionId' | 'url') => void;
}

export function ResourcePicker({ type, value, onChange }: ResourcePickerProps) {
  // Fetch Pages
  const { data: pages } = useQuery({
    queryKey: ['store-pages'],
    queryFn: async () => {
      const res: any = await apiClient.get('/merchant/pages');
      return res; // unwrapped by client
    },
    enabled: type === 'PAGE',
  });

  // Future: Fetch Products
  const { data: products } = useQuery({
    queryKey: ['store-products'],
    queryFn: async () => {
      const res: any = await apiClient.get('/merchant/products');
      return res;
    },
    enabled: type === 'PRODUCT',
  });

  // Future: Fetch Collections/Categories
  const { data: collections } = useQuery({
    queryKey: ['store-collections'],
    queryFn: async () => {
      const res: any = await apiClient.get('/merchant/categories');
      return res;
    },
    enabled: type === 'COLLECTION',
  });

  if (type === 'PAGE') {
    return (
      <Select value={value || ''} onValueChange={(val) => onChange(val as string, 'pageId')}>
        <SelectTrigger>
          <SelectValue placeholder="Select a page" />
        </SelectTrigger>
        <SelectContent>
          {pages?.map((page: any) => (
            <SelectItem key={page.id} value={page.id}>
              {page.title} ({page.slug})
            </SelectItem>
          ))}
          {!pages?.length && <SelectItem value="none" disabled>No pages found</SelectItem>}
        </SelectContent>
      </Select>
    );
  }

  if (type === 'PRODUCT') {
    return (
      <Select value={value || ''} onValueChange={(val) => onChange(val as string, 'productId')}>
        <SelectTrigger>
          <SelectValue placeholder="Select a product" />
        </SelectTrigger>
        <SelectContent>
          {products?.map((prod: any) => (
            <SelectItem key={prod.id} value={prod.id}>
              {prod.name}
            </SelectItem>
          ))}
          {!products?.length && <SelectItem value="none" disabled>No products found</SelectItem>}
        </SelectContent>
      </Select>
    );
  }

  if (type === 'COLLECTION') {
    return (
      <Select value={value || ''} onValueChange={(val) => onChange(val as string, 'collectionId')}>
        <SelectTrigger>
          <SelectValue placeholder="Select a collection" />
        </SelectTrigger>
        <SelectContent>
          {collections?.map((col: any) => (
            <SelectItem key={col.id} value={col.id}>
              {col.name}
            </SelectItem>
          ))}
          {!collections?.length && <SelectItem value="none" disabled>No collections found</SelectItem>}
        </SelectContent>
      </Select>
    );
  }

  // Fallback for EXTERNAL / CUSTOM
  return (
    <Input
      placeholder="e.g. https://example.com"
      value={value || ''}
      onChange={(e) => onChange(e.target.value, 'url')}
    />
  );
}
